import axios from "axios";
import { FileText, Pencil, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

import BlogCreateModal from "../../../components/AdminSideComponents/blogs/BlogCreateModal";
import BlogDeleteConfirm from "../../../components/AdminSideComponents/blogs/BlogDeleteConfirm";
import BlogEditModal from "../../../components/AdminSideComponents/blogs/BlogEditModal";
import Breadcrumb from "../../../components/AdminSideComponents/BreadCrumb";

const AdminBlog = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);

  // Fetch all blogs
  const fetchBlogs = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/blogs/get`);
      setBlogs(res.data);
    } catch (err) {
      console.error("Failed to fetch blogs", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const handleSuccess = () => {
    fetchBlogs();
  };

  // Open edit modal
  const handleEdit = (blog) => {
    setSelectedBlog(blog);
    setEditOpen(true);
  };

  // Open delete confirmation
  const handleDeleteClick = (blog) => {
    setSelectedBlog(blog);
    setDeleteOpen(true);
  };

  // Confirm delete
  const handleDelete = async () => {
    if (!selectedBlog) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/blogs/${selectedBlog._id}`
      );
      handleSuccess();
      setDeleteOpen(false);
      setSelectedBlog(null);
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Failed to delete blog");
    }
  };

  return (
    <div className="bg-gradient-to-br px-6 min-h-screen from-[#FFF7F3] to-gray-50">
      {/* Header */}
      <div className="py-2">
        <Breadcrumb
          title1={"Blogs"}
          title2={"Manage Blogs"}
          showBtn={true}
          buttonName={"Add New"}
          onBtnClick={() => setCreateOpen(true)}
        />
      </div>
      {/* Blog Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="h-64 rounded-xl bg-gray-200 animate-pulse"
            />
          ))}
        </div>
      ) : blogs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-5">
            <FileText className="w-10 h-10 text-gray-500" />
          </div>

          <h3 className="text-xl font-semibold text-gray-800">No blogs yet</h3>

          <p className="text-gray-500 mt-2 max-w-md">
            You haven’t published any blogs. Start creating content and it will
            appear here.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {blogs.map((blog) => (
            <div
              key={blog._id}
              className="group relative bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300"
            >
              {blog.image && (
                <img
                  src={blog.image}
                  alt={blog.category}
                  className="h-48 w-full object-cover"
                />
              )}

              <div className="p-5">
                <span className="text-xs font-semibold text-orange-500 uppercase tracking-wide">
                  {blog.category}
                </span>
                <p className="mt-2 text-gray-700 line-clamp-3">
                  {blog.description}
                </p>
              </div>

              {/* Hover Actions - Edit & Delete */}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-6">
                <button
                  onClick={() => handleEdit(blog)}
                  className="bg-red-500 p-3 rounded-full hover:bg-red-600  transition"
                  title="Edit Blog"
                >
                  <Pencil className="w-5 h-5 text-white" />
                </button>

                <button
                  onClick={() => handleDeleteClick(blog)}
                  className="bg-red-500 p-3 rounded-full hover:bg-red-600 transition"
                  title="Delete Blog"
                >
                  <Trash2 className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      <BlogCreateModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={handleSuccess}
      />

      <BlogEditModal
        isOpen={editOpen}
        onClose={() => {
          setEditOpen(false);
          setSelectedBlog(null);
        }}
        blog={selectedBlog}
        onSuccess={handleSuccess}
      />

      <BlogDeleteConfirm
        isOpen={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setSelectedBlog(null);
        }}
        blog={selectedBlog}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default AdminBlog;
